import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSurveySections, SurveySection } from 'src/api/dataVault/survey';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  sections: SurveySection[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  bySurveyId: Record<number, Entry>;
}

const STALE_MS = 30_000;
const initialState: State = { bySurveyId: {} };

export const fetchSurveySections = createAsyncThunk(
  'surveySections/fetch',
  async (
    {
      surveyId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      surveyId: number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { surveySections: State }).surveySections;
    const entry = state.bySurveyId[surveyId];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null;
    }
    try {
      return await getSurveySections(surveyId, { actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load survey sections'
      );
    }
  }
);

const slice = createSlice({
  name: 'surveySections',
  initialState,
  reducers: {
    invalidateSurvey(state, action: PayloadAction<number>) {
      delete state.bySurveyId[action.payload];
    },
    clearAll(state) {
      state.bySurveyId = {};
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSurveySections.pending, (state, action) => {
        const id = action.meta.arg.surveyId;
        const forceRefresh = action.meta.arg.forceRefresh ?? false;
        const entry = state.bySurveyId[id];
        if (
          !forceRefresh &&
          entry?.status === 'succeeded' &&
          entry.lastFetchedAt &&
          Date.now() - entry.lastFetchedAt < STALE_MS
        ) {
          return;
        }
        state.bySurveyId[id] = {
          status: 'loading',
          sections: [],
          error: null,
          lastFetchedAt: null,
        };
      })
      .addCase(fetchSurveySections.fulfilled, (state, action) => {
        const id = action.meta.arg.surveyId;
        if (action.payload === null) {
          return;
        }
        state.bySurveyId[id] = {
          status: 'succeeded',
          sections: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchSurveySections.rejected, (state, action) => {
        const id = action.meta.arg.surveyId;
        state.bySurveyId[id] = {
          status: 'failed',
          sections: [],
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSurvey, clearAll } = slice.actions;

export const selectSurveySections =
  (surveyId: number) =>
  (state: { surveySections: State }): Entry | undefined =>
    state.surveySections.bySurveyId[surveyId];

export default slice.reducer;
